import { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

/**
 * ---------------------------------------------------------
 * GameSettingsPanel
 * ---------------------------------------------------------
 * Props:
 * - title: string – page title (e.g., "Time Trial")
 * - settings: Array<{
 *     key: string;                  // key that will appear in the dict
 *     label?: string;               // readable label (defaults to key)
 *     options: Array<string | { label: string; value: any }>
 *   }>
 * - initialValues?: Record<string, any>   // optional starting dict
 * - onChange?: (dict) => void             // called whenever dict changes
 *
 * Behavior:
 * - Renders a list row for each setting with < and > arrow buttons to cycle
 *   through options. Keeps a dict of { [key]: value } in state and calls onChange.
 */

function normalizeOptions(options) {
  return options.map((opt) =>
    typeof opt === "string" ? { label: opt, value: opt } : opt
  );
}

function SettingRow({ idPrefix, label, valueLabel, valueColor, onPrev, onNext }) {
  return (
    <Stack
      id={`${idPrefix}-setting`}
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ width: "100%" }}
    >
      <IconButton
        id={`${idPrefix}-previous`}
        onClick={onPrev}
        size="large"
        color="primary"
        sx={{
          border: "2px solid",
          borderColor: "primary.main",
          bgcolor: "rgba(0,229,255,0.06)",
          '&:hover': { bgcolor: "rgba(0,229,255,0.12)" },
        }}
      >
        <ArrowBackIosNewIcon />
      </IconButton>

      <Box id={`${idPrefix}-value`} sx={{ flex: 1 }}>
        <Typography variant="overline" center sx={{ color: "text.secondary" }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
          {valueColor && (
            <Box
              aria-hidden="true"
              sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: valueColor, flexShrink: 0 }}
            />
          )}
          <Typography variant="h5" center sx={{ fontWeight: 800, letterSpacing: 1 }}>
            {valueLabel}
          </Typography>
        </Stack>
      </Box>

      <IconButton
        id={`${idPrefix}-next`}
        onClick={onNext}
        size="large"
        color="secondary"
        sx={{
          border: "2px solid",
          borderColor: "secondary.main",
          bgcolor: "rgba(255,45,149,0.06)",
          '&:hover': { bgcolor: "rgba(255,45,149,0.12)" },
        }}
      >
        <ArrowForwardIosIcon />
      </IconButton>
    </Stack>
  );
}

function GameSettingsPanel({ idPrefix = 'game-settings', title, settings, initialValues = {}, onChange,OnStart }) {
  const normalized = useMemo(
    () => settings.map((s) => ({ ...s, options: normalizeOptions(s.options), label: s.label || s.key })),
    [settings]
  );

  // Build starting state from initialValues or the first option for each setting
  const starting = useMemo(() => {
    const result = {};
    normalized.forEach((s) => {
      if (initialValues[s.key] !== undefined) result[s.key] = initialValues[s.key];
      else result[s.key] = s.options[0]?.value;
    });
    return result;
  }, [normalized, initialValues]);

  const [dict, setDict] = useState(starting);
  const [indices, setIndices] = useState(() => {
    const idx = {};
    normalized.forEach((s) => {
      const i = s.options.findIndex((o) => o.value === starting[s.key]);
      idx[s.key] = i >= 0 ? i : 0;
    });
    return idx;
  });

  const handleCycle = (key, dir) => {
    setIndices((prev) => {
      const max = normalized.find((s) => s.key === key).options.length;
      let next = (prev[key] + (dir === "next" ? 1 : -1) + max) % max;
      const nextIndices = { ...prev, [key]: next };

      // also update dict
      const opts = normalized.find((s) => s.key === key).options;
      const nextValue = opts[next].value;
      setDict((d) => {
        const nd = { ...d, [key]: nextValue };
        onChange && onChange(nd);
        return nd;
      });

      return nextIndices;
    });
  };

  return (
    <Card
      id={`${idPrefix}-panel`}
      elevation={8}
      sx={{
        width: 680,
        maxWidth: "95vw",
        borderRadius: 6,
        mx: "auto",
        backdropFilter: "blur(6px)",
        border: "2px solid",
        borderColor: "primary.main",
        boxShadow: `0 0 24px rgba(0,229,255,0.25), 0 0 48px rgba(57,255,20,0.08)`
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 900,
            textAlign: "center",
            letterSpacing: 2,
            mb: 2,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>

        <Divider sx={{ opacity: 0.2, mb: 3 }} />

        <Stack spacing={2.5}>
          {normalized.map((s) => {
            const idx = indices[s.key] ?? 0;
            const valueLabel = s.options[idx]?.label ?? "";
            const valueColor = s.options[idx]?.color ?? null;
            return (
              <SettingRow
                key={s.key}
                idPrefix={`${idPrefix}-${s.key}`}
                label={s.label}
                valueLabel={valueLabel}
                valueColor={valueColor}
                onPrev={() => handleCycle(s.key, "prev")}
                onNext={() => handleCycle(s.key, "next")}
              />
            );
          })}
        </Stack>

        <Button
          id={`${idPrefix}-start`}
          fullWidth
          size="large"
          variant="contained"
          sx={{ mt: 3, fontWeight: 800, letterSpacing: 1 }}
          onClick={() => OnStart(dict)}
        >
          Start
        </Button>
      </CardContent>
    </Card>
  );
}

export default GameSettingsPanel;
